import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    TextField,
    Button,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Alert,
    Tooltip,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from './config';

const EditTripForm = ({ trip, tripId, onSaved }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    
    const [form, setForm] = useState({
        name: '',
        duration_days: 7,
        start_date: '',
        end_date: '',
        start_month: new Date().getMonth() + 1,
    });
    
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [hasLockedContent, setHasLockedContent] = useState(false);
    const [checkingLocks, setCheckingLocks] = useState(true);
    const [formInitialized, setFormInitialized] = useState(false);
    
    const isFlexible = !trip?.start_date;
    
    // Update form when trip data loads (only once)
    useEffect(() => {
        if (trip && !formInitialized) {
            setForm({
                name: trip.name || '',
                duration_days: parseInt(trip.duration_days) || 7,
                start_date: trip.start_date || '',
                end_date: trip.end_date || '',
                start_month: parseInt(trip.month_of_year) || new Date().getMonth() + 1,
            });
            setFormInitialized(true);
        }
    }, [trip, formInitialized]);
    
    // Check if any days or activities are locked
    useEffect(() => {
        const checkLockedContent = async () => {
            setCheckingLocks(true);
            try {
                const token = localStorage.getItem('token');
                
                // Check locked days
                const daysResponse = await fetch(`${API_BASE_URL}/days/get_locked.php?trip_id=${tripId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const lockedDays = await daysResponse.json();
                // Use == to handle both string "1" and number 1
                const hasLockedDays = lockedDays.some(day => day.is_locked == 1);
                
                // Check pinned activities
                const activitiesResponse = await fetch(`${API_BASE_URL}/activities.php?trip_id=${tripId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const activities = await activitiesResponse.json();
                
                // Helper to check if a flight has actual flight info (flight number, times)
                const hasActualFlightInfo = (act) => {
                    if (act.type !== 'transportation' && act.type !== 'flight') return false;
                    try {
                        const data = typeof act.structured_data === 'string' 
                            ? JSON.parse(act.structured_data) 
                            : act.structured_data;
                        // Flight is "locked" if it has carrier code AND flight number AND departure time
                        return data && (data.carrierCode || data.carrier_code) && 
                               (data.flightNumber || data.flight_number) &&
                               (data.departureDateTime || data.departure_time);
                    } catch {
                        return false;
                    }
                };
                
                // Check pinned non-flight activities OR flights with actual flight info
                const hasPinnedActivities = activities.some(act => {
                    if (act.is_pinned != 1) return false;
                    // For transportation/flights, only count as locked if has actual flight details
                    if (act.type === 'transportation' || act.type === 'flight') {
                        return hasActualFlightInfo(act);
                    }
                    // Non-transportation pinned activities are always locked
                    return true;
                });
                
                // Check activities with bookings (reference_ticket_no)
                const hasBookedActivities = activities.some(act => act.reference_ticket_no);
                
                setHasLockedContent(hasLockedDays || hasPinnedActivities || hasBookedActivities);
            } catch (err) {
                console.error('Error checking locks:', err);
            } finally {
                setCheckingLocks(false);
            }
        };
        
        if (tripId && !isFlexible) {
            checkLockedContent();
        } else {
            setCheckingLocks(false);
        }
    }, [tripId, isFlexible]);
    
    // Generate month options
    const generateMonthOptions = () => {
        const months = [];
        const now = new Date();
        for (let i = 0; i < 12; i++) {
            const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
            months.push({
                value: date.getMonth() + 1,
                label: date.toLocaleDateString('default', { month: 'long', year: 'numeric' })
            });
        }
        return months;
    };
    
    // Calculate end date when start date or duration changes
    const calculateEndDate = (startDate, duration) => {
        if (!startDate || !duration) return '';
        const start = new Date(startDate);
        const end = new Date(start);
        end.setDate(start.getDate() + parseInt(duration) - 1);
        return end.toISOString().split('T')[0];
    };
    
    const handleStartDateChange = (e) => {
        const newStartDate = e.target.value;
        setForm(prev => ({
            ...prev,
            start_date: newStartDate,
            end_date: calculateEndDate(newStartDate, prev.duration_days)
        }));
    };
    
    const handleDurationChange = (e) => {
        const value = e.target.value;
        // Allow empty string while typing, parse to number for calculations
        const newDuration = value === '' ? '' : parseInt(value);
        setForm(prev => ({
            ...prev,
            duration_days: newDuration,
            end_date: isFlexible ? '' : calculateEndDate(prev.start_date, newDuration || 7)
        }));
    };
    
    const handleSave = async () => {
        if (!form.name.trim()) {
            setError(t('trip_name_required'));
            return;
        }
        
        setSaving(true);
        setError('');
        
        try {
            const token = localStorage.getItem('token');
            const originalDuration = parseInt(trip?.duration_days) || 7;
            const newDuration = parseInt(form.duration_days) || 7;
            const durationChanged = newDuration !== originalDuration;
            let needsGeneration = false;
            
            // If duration changed, call adjust_duration API first
            if (durationChanged) {
                const adjustResponse = await fetch(`${API_BASE_URL}/trips/adjust_duration.php`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        trip_id: tripId,
                        new_duration: newDuration
                    })
                });
                
                const adjustData = await adjustResponse.json();
                
                if (!adjustData.success) {
                    setError(adjustData.message || t('trip_duration_adjust_failed'));
                    setSaving(false);
                    return;
                }
                
                needsGeneration = adjustData.needs_generation;
            }
            
            const payload = {
                name: form.name,
                start_location: trip.start_location || '',
                end_location: trip.end_location || '',
                duration_days: newDuration,
            };
            
            if (isFlexible) {
                payload.month_of_year = form.start_month;
                payload.start_date = null;
                payload.end_date = null;
            } else {
                payload.start_date = form.start_date;
                payload.end_date = form.end_date;
            }
            
            const response = await fetch(`${API_BASE_URL}/trips/update.php?trip_id=${tripId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });
            
            if (response.ok) {
                // Navigate to trip itinerary page - if generation needed, pass state
                if (needsGeneration) {
                    navigate(`/trip/${tripId}`, { state: { triggerGeneration: true } });
                } else {
                    navigate(`/trip/${tripId}`);
                }
            } else {
                const data = await response.json();
                setError(data.message || t('trip_update_failed'));
            }
        } catch (err) {
            setError(t('trip_update_failed'));
        } finally {
            setSaving(false);
        }
    };
    
    const handleCancel = () => {
        navigate(`/trip/${tripId}`);
    };
    
    if (checkingLocks) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }
    
    const canEditDates = isFlexible || !hasLockedContent;
    
    return (
        <Box sx={{ maxWidth: 600, mx: 'auto', p: 3 }}>
            <Typography variant="h5" gutterBottom>
                {t('edit_trip_title')}
            </Typography>
            
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}
            
            {/* Trip Name - Always editable */}
            <TextField
                fullWidth
                label={t('wizard_trip_name')}
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                margin="normal"
                required
            />
            
            {/* Flexible Trip: Month and Duration */}
            {isFlexible && (
                <>
                    <FormControl fullWidth margin="normal">
                        <InputLabel>{t('wizard_preferred_month')}</InputLabel>
                        <Select
                            value={form.start_month}
                            onChange={(e) => setForm(prev => ({ ...prev, start_month: e.target.value }))}
                            label={t('wizard_preferred_month')}
                        >
                            {generateMonthOptions().map(option => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    
                    <TextField
                        fullWidth
                        label={t('wizard_duration_days')}
                        type="number"
                        value={form.duration_days}
                        onChange={handleDurationChange}
                        margin="normal"
                        inputProps={{ min: 1, max: 365 }}
                    />
                </>
            )}
            
            {/* Fixed Date Trip: Start Date and Duration (if not locked) */}
            {!isFlexible && (
                <>
                    {hasLockedContent ? (
                        <Box sx={{ mt: 2, p: 2, bgcolor: '#fff3e0', borderRadius: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LockIcon color="warning" />
                                <Typography variant="body2" color="text.secondary">
                                    {t('dates_locked_message')}
                                </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                <strong>{t('wizard_start_date')}:</strong> {form.start_date}
                            </Typography>
                            <Typography variant="body2">
                                <strong>{t('wizard_end_date')}:</strong> {form.end_date}
                            </Typography>
                            <Typography variant="body2">
                                <strong>{t('wizard_duration_days')}:</strong> {form.duration_days}
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <TextField
                                fullWidth
                                type="date"
                                label={t('wizard_start_date')}
                                value={form.start_date}
                                onChange={handleStartDateChange}
                                margin="normal"
                                InputLabelProps={{ shrink: true }}
                            />
                            
                            <TextField
                                fullWidth
                                label={t('wizard_duration_days')}
                                type="number"
                                value={form.duration_days}
                                onChange={handleDurationChange}
                                margin="normal"
                                inputProps={{ min: 1, max: 365 }}
                            />
                            
                            <TextField
                                fullWidth
                                type="date"
                                label={t('wizard_end_date')}
                                value={form.end_date}
                                margin="normal"
                                InputLabelProps={{ shrink: true }}
                                disabled
                                helperText={t('end_date_calculated')}
                            />
                        </>
                    )}
                </>
            )}
            
            {/* Action Buttons */}
            <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={saving || !form.name.trim()}
                    fullWidth
                >
                    {saving ? <CircularProgress size={24} /> : t('wizard_save_changes')}
                </Button>
                <Button
                    variant="outlined"
                    onClick={handleCancel}
                    disabled={saving}
                    fullWidth
                >
                    {t('cancel')}
                </Button>
            </Box>
        </Box>
    );
};

export default EditTripForm;
